import { useState } from "react";
import { Button, Input, Textarea, Toast } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { PageHeader } from "@/components/PageHeader";
import { cms } from "@/api/cms.service";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import {
  DESCRIPTION_MAX_LENGTH,
  HOMEPAGE_SLUG,
  TITLE_MAX_LENGTH,
  readHomepageContent,
  validateLaunchTile,
  withLaunchTile,
  type HomepageContent,
  type LaunchTileErrors,
  type LaunchTileValues,
} from "./homepageLaunchTile";
import styles from "./HomepagePage.module.css";

const TITLE_INPUT_ID = "launch-tile-title";

const NOT_SET_UP_MESSAGE = "Homepage content isn't set up. Contact a developer.";

const TITLE_HINT = "Don't include \"New\"; it's added automatically.";

type ToastState = {
  message: string;
  type: ToastType;
};

type LaunchTileFormProps = {
  content: HomepageContent;
  canWrite: boolean;
  onSaved: () => void;
  onSaveFailed: (message: string) => void;
};

async function loadHomepageContent(): Promise<HomepageContent | null> {
  const { pages } = await cms.getPages({ slug: HOMEPAGE_SLUG });
  const page = pages.find(candidate => candidate.slug === HOMEPAGE_SLUG) ?? null;

  if (!page) {
    return null;
  }

  const blocks = await cms.getBlocksByPageId(page.id);

  return readHomepageContent(page, blocks);
}

function LaunchTileForm({ content, canWrite, onSaved, onSaveFailed }: LaunchTileFormProps) {
  const [values, setValues] = useState<LaunchTileValues>(content.launchTile);
  const [errors, setErrors] = useState<LaunchTileErrors>({});
  const [saving, setSaving] = useState(false);

  const setField = (key: keyof LaunchTileValues, value: string) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: undefined }));
  };

  const handleSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();

    const nextErrors = validateLaunchTile(values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSaving(true);

    try {
      const freshBlocks = await cms.getBlocksByPageId(content.block.pageId);
      const freshBlock = freshBlocks.find(block => block.id === content.block.id);
      const updatedContent = freshBlock ? withLaunchTile(freshBlock, values) : null;

      if (!updatedContent) {
        onSaveFailed(NOT_SET_UP_MESSAGE);
        return;
      }

      await cms.updateBlock(content.block.id, { content: updatedContent });
      onSaved();
    } catch (error) {
      onSaveFailed(error instanceof Error ? error.message : "The launch tile could not be saved");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {!content.isPublished && (
        <p className={styles.warning} role="status">
          The homepage content isn't published, so the launch tile isn't live on the site.
        </p>
      )}

      <section className={styles.section} aria-labelledby="launch-tile-heading">
        <h2 className={styles.sectionHeading} id="launch-tile-heading">
          Launch tile
        </h2>

        <div className={styles.titleField}>
          <label className={styles.label} htmlFor={TITLE_INPUT_ID}>
            Title
          </label>
          <div className={styles.titleRow}>
            <span className={styles.titlePrefix}>New</span>
            <Input
              id={TITLE_INPUT_ID}
              className={styles.titleInput}
              value={values.title}
              error={errors.title}
              helperText={TITLE_HINT}
              maxLength={TITLE_MAX_LENGTH}
              fullWidth
              disabled={!canWrite}
              onChange={e => setField("title", e.target.value)}
            />
          </div>
        </div>

        <Textarea
          label="Description"
          value={values.text}
          error={errors.text}
          maxLength={DESCRIPTION_MAX_LENGTH}
          fullWidth
          rows={3}
          disabled={!canWrite}
          onChange={e => setField("text", e.target.value)}
        />

        <Input
          label="Link"
          value={values.href}
          error={errors.href}
          fullWidth
          disabled={!canWrite}
          helperText="A legal500.com link, or a path on the site such as /rankings."
          onChange={e => setField("href", e.target.value)}
        />
      </section>

      <p className={styles.note}>Changes appear on the site after the next site sync.</p>

      <div className={styles.formActions}>
        <Button type="submit" variant="contained" color="primary" disabled={!canWrite || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

export function HomepagePage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("cms.write");
  const [toast, setToast] = useState<ToastState | null>(null);

  const { data, loading, error, refetch } = useQuery(() => loadHomepageContent(), []);

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Homepage" />

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={loading} />}

      {loading ? (
        <LoadingState message="Loading the homepage…" />
      ) : data ? (
        <LaunchTileForm
          key={data.block.updatedAt}
          content={data}
          canWrite={canWrite}
          onSaved={() => {
            setToast({ message: "Launch tile saved.", type: "success" });
            refetch();
          }}
          onSaveFailed={message => setToast({ message, type: "error" })}
        />
      ) : (
        !error && <EmptyState variant="error" title={NOT_SET_UP_MESSAGE} />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

export default HomepagePage;
